import type { QueryClient } from '@tanstack/react-query';
import { Outlet, createRootRouteWithContext } from '@tanstack/react-router';
import type { ComponentType } from 'react';
import { Suspense, lazy } from 'react';
import { isDev } from '../lib/dev-flag';

// Devtools are loaded lazily so the production bundle never pulls them in.
//
// Tree-shaking strategy (two gates, both required):
//
//   1. `import.meta.env.DEV` (the LITERAL, not the `isDev()` indirection) on
//      the `lazy(() => import(...))` call. Vite inlines `DEV` at transform
//      time, so in a production build the conditional collapses to a stub
//      `() => null` component. Because the dynamic-import call is then
//      unreachable, Rollup drops the devtools chunks from `dist/`.
//
//   2. `isDev()` (function indirection) on the runtime render path. This
//      gives tests a `vi.spyOn(devFlag, 'isDev')` seam so they can prove
//      the gate works without rebuilding with `--mode development`. In
//      production this branch is also `false` and bails before touching
//      the stubbed lazy components.
//
// Removing either gate breaks something:
//   - Removing (1): devtools bundle ships to users (the supply-chain
//     incident on 2026-05-11 is exactly why this matters).
//   - Removing (2): tests cannot toggle the gate, and we lose the
//     "devtools never render in test" guarantee.
const NullComponent: ComponentType<unknown> = () => null;

const RouterDevtools = import.meta.env.DEV
  ? lazy(() =>
      import('@tanstack/react-router-devtools').then((m) => ({
        default: m.TanStackRouterDevtools,
      })),
    )
  : NullComponent;

const QueryDevtools = import.meta.env.DEV
  ? lazy(() =>
      import('@tanstack/react-query-devtools').then((m) => ({
        default: m.ReactQueryDevtools,
      })),
    )
  : NullComponent;

function Devtools() {
  if (!isDev()) return null;
  return (
    <Suspense fallback={null}>
      <RouterDevtools position="bottom-right" />
      <QueryDevtools buttonPosition="bottom-left" />
    </Suspense>
  );
}

/**
 * Router-typed context. PR3 supplies a `QueryClient` so route loaders can
 * call `ensureSession(context.queryClient)` and any other future server-state
 * helpers without reaching for a singleton.
 */
export type RouterContext = {
  queryClient: QueryClient;
};

type ProblemLike = {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  requestId?: string;
};

function isProblemLike(value: unknown): value is ProblemLike {
  if (typeof value !== 'object' || value === null) return false;
  // Read keys defensively; we only render strings/numbers we can verify.
  return 'requestId' in value || 'status' in value || 'title' in value || 'detail' in value;
}

function extractString(record: ProblemLike, key: keyof ProblemLike): string | undefined {
  const value = record[key];
  return typeof value === 'string' ? value : undefined;
}

function extractNumber(record: ProblemLike, key: keyof ProblemLike): number | undefined {
  const value = record[key];
  return typeof value === 'number' ? value : undefined;
}

function RootErrorComponent({ error }: { error: unknown }) {
  if (isProblemLike(error)) {
    const title = extractString(error, 'title') ?? 'Something went wrong';
    const status = extractNumber(error, 'status');
    const detail = extractString(error, 'detail');
    const requestId = extractString(error, 'requestId');
    return (
      <main role="alert" data-testid="root-error">
        <h1>{title}</h1>
        {status !== undefined ? <p>Status: {status}</p> : null}
        {detail ? <p>{detail}</p> : null}
        {requestId ? <p data-testid="root-error-request-id">Request ID: {requestId}</p> : null}
      </main>
    );
  }

  const message = error instanceof Error ? error.message : 'Unexpected error';
  return (
    <main role="alert" data-testid="root-error">
      <h1>Something went wrong</h1>
      <p>{message}</p>
    </main>
  );
}

function NotFoundComponent() {
  return (
    <main role="alert" data-testid="root-not-found">
      <h1>Page not found</h1>
    </main>
  );
}

function RootComponent() {
  return (
    <>
      <Outlet />
      <Devtools />
    </>
  );
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
  errorComponent: RootErrorComponent,
  notFoundComponent: NotFoundComponent,
});
